import { data as routerData } from 'react-router';
import type { LoaderFunctionArgs } from '~/types/route-args';
import type { SerializeFrom } from '~/types/serialize-from';
import { useEffect, useMemo } from 'react';
import { useLoaderData, useLocation } from 'react-router';
import { useSetAtom } from 'jotai';
import { cn } from '@rocco/utils/cn';
import { getImagePreloadLink, getPreloadImageLink } from '~/modules/images';
import {
    createMetaDescription,
    createOGTags,
    fallbackOGSocialImage,
    getCanonicalHreflang,
    mergeMeta,
} from '~/modules/seo';
import { createMetaTitle } from '~/modules/seo';
import { createArticleSchema } from '~/modules/seo/schema/article-schema';
import { createFAQSchema } from '~/modules/seo/schema/faq-page-schema';
import { createLDSchema } from '~/modules/seo/util/create-ld-schema';
import { getImagesSizes } from '~/utils/image-sizes-utils';
import { ArticleDetails, ArticleIntro } from '@modules/article';
import {
    useAppDisplayModeContext,
    Container,
    useStyles,
} from '@archipro-design/aria';
import type {
    GetArticleDetailsQuery,
    GetArticleDetailsQueryVariables,
} from 'generated/graphql';
import { PermissionEnum } from 'generated/graphql';
import type { PageBuilderModuleType } from '@modules/page-builder/types';
import { ArticleRevampPage } from '~/modules/article-revamp';
import { getArticleRevampData } from '~/modules/article-revamp/api/get-article-revamp-data.server';
import { isArticleRevampEnabled } from '~/modules/article-revamp/util/is-article-revamp-enabled';
import { getRevampHero } from '~/modules/article-revamp/util/revamp-image-sizes';
import DetailModules from '@modules/page-builder/component/detail-article/detail-modules/DetailModules';
import { GetArticleDetailsDocument } from 'generated/graphql';
import { assertResponse } from '~/modules/root/graphql/assertResponse.server';
import { getModules } from '~/modules/page-builder/api/get-modules.server';
import DetailBannerSection from '@modules/page-builder/component/detail-article/detail-banner-section/DetailBannerSection';
import { DetailPageButtons } from '@modules/business-manager/component/detail-page-buttons/DetailPageButtons';
import { ProductTaggingContextProvider } from '~/modules/product-tagging/context';
import ArticleFooter from '@modules/article/component/article-footer/ArticleFooter';
import {
    getCanDownloadImages,
    getPermissions,
    useEditPermissions,
    usePermissions,
} from '@modules/user';
import getMemberRecentlyTagged from '~/modules/product-tagging/api/get-member-recently-tagged.server';
import { PaintPickerContextProvider as PaintPickerContextProviderV2 } from '@modules/paint-picker-new/context/PaintPickerContext';
import { getPageLocalisation } from '~/modules/seo/api/get-page-localisation.server';
import { unsafeVariant_DO_NOT_USE } from '~/utils/unsafeVariant';
import {
    FULL_COLUMN_WIDTH,
    FULL_COLUMN_WIDTH_MOBILE,
} from '~/modules/page-builder/config/column-constants';
import DownloadAction from '~/modules/page-builder/component/detail-article/detail-banner-section/actions/DownloadAction';
import { useRefreshOnRotate } from '@modules/root/util/use-refresh-on-rotate';
import { getBannerDimensions } from '~/utils/banner-utils';
import { getVideoBanner } from '@modules/page-builder/api/get-video-banner.server';
import { useSetEditMode } from '@modules/root/hook/use-set-edit-mode';
import { createWarningMessages } from '@modules/root/hook/use-warning-messages';
import makeArticleLink from '@modules/article/util/make-article-link';
import { useArticlePreviewRouteData } from '@modules/article/hook/use-article-preview-route-data';
import * as S from '@modules/article/component/article-details/ArticleDetails.style';
import { adsRequest } from '~/modules/adverts/api';
import { getProjectPlacements } from '~/modules/adverts/utils/project-placement-helper.server';
import { useLocationMemo } from '~/modules/adverts/hook/use-location-memo';
import { useHydrated } from 'remix-utils';
import getModuleWithAds from '~/modules/article/util/get-module-with-ads';
import { makeArticleItemData } from '~/modules/adverts/utils/get-item-data.server';
import {
    articleAvailableCountries,
    articleCanonicalCountry,
    isArticleIndexableInCountry,
} from '~/modules/seo/util/article-indexability';
import { getBMPagePath } from '~/modules/root/util/get-bm-page-path';
import { getRouteData } from '~/modules/root/util/get-route-data';
import { getSession } from '~/modules/root/util/session.server';
import { useArticleGating } from '~/modules/article/hook/use-article-gating';
import { isTodayOrPastDate } from '@modules/date-time';
import { collectTextFromModules } from '@modules/page-builder/util/get-modules-text';
import { getMemberTagPermissions } from '~/modules/page-builder/util/get-tag-permissions.server';
import { ModulesContextProvider } from '~/modules/page-builder/context/modules-context';
import {
    getArticleDetails,
    getArticleMeta,
} from '~/modules/article/api/get-article-details.server';
import { setCloneCountryStateAtom } from '~/state/ui-state-clone-country-link';
import type { APHandle } from '@modules/root/type/handle';
import React from 'react';
import { useCurrentCategory } from '@rocco/ui/website-header/hooks/use-current-category';

const MAX_ADS = 2;
const AD_GAP = 3;

export const meta = mergeMeta<typeof loader>(
    () => {
        return [];
    },
    (props) => {
        const { data, matches } = props;

        if (!data?.details?.title) {
            return [];
        }

        const { rootData } = getRouteData(matches);

        const title = createMetaTitle(
            props,
            [data?.meta?.Title || data.details.title],
            true,
            1,
            false,
        );

        const hasIntroText = data?.details?.introText?.length > 0;
        const description = createMetaDescription([
            data?.localisation?.Description,
            hasIntroText
                ? data?.details?.introText
                : collectTextFromModules(data?.modules ?? [], 255),
        ]);

        const fallbackSocialImage = fallbackOGSocialImage(
            props,
            data?.banner?.libraryImage?.LibraryImageFile?.DetailBannerStandard
                ?.Desktop,
        );

        // ToDo: Video schema
        const articleSchema = createArticleSchema(props);
        const FAQSchema = createFAQSchema(data.localisation?.FAQSchemas ?? []);
        const ldSchema = createLDSchema([articleSchema ?? [], FAQSchema]);

        const bannerImage = data?.banner.imageUrl;
        // The revamp's title-section tiers render different images at different
        // widths — premium has its own portrait banner, not the landscape
        // TopBanner — so the hero is resolved through the same helper the view
        // branches on, then built with the same loader the image uses. Both
        // sides then resolve to one URL and the hero is fetched once. The
        // legacy page keeps its device-split preload.
        const revampHero = data?.revamp
            ? getRevampHero({
                  ...data.revamp,
                  bannerImageUrl: bannerImage,
                  premiumBannerImageUrl: data.details.premiumBannerImageUrl,
              })
            : undefined;
        const preloadImageLinks = data?.revamp
            ? getImagePreloadLink(revampHero?.src, {
                  sizes: revampHero?.sizes,
              })
            : data?.deviceType === 'mobile'
              ? getPreloadImageLink(undefined, bannerImage, getImagesSizes(378))
              : getPreloadImageLink(
                    bannerImage,
                    undefined,
                    getImagesSizes(1820),
                );

        // Indexability is driven by the article's availability zones: an
        // article is only indexable on the markets it is genuinely available
        // in. This single source of truth feeds both the robots tag and the
        // hreflang alternates, so an AU/NZ article served on the US domain is
        // noindexed and advertises no en-us alternate.
        const availableCountries = articleAvailableCountries(
            data?.localisation,
        );
        const indexable = isArticleIndexableInCountry(
            rootData?.localisation?.country,
            availableCountries,
        );
        const noIndex = Boolean(data?.noIndex) || !indexable;

        // Resolve the canonical home from the article's availability zones. The
        // professional's country is only used when the article is actually
        // available there: editorial accounts can have Country = NZ while
        // publishing into AU + US zones, so falling back to the professional
        // country would canonicalise to a host where the article does not exist
        // while the hreflang alternates point at the real markets.
        const canonicalCountry = articleCanonicalCountry(
            data.details.professional.Country,
            availableCountries,
        );

        return [
            { title },
            { name: 'description', content: description },
            ...(noIndex ? [{ name: 'robots', content: 'noindex' }] : []),
            ...createOGTags({
                title,
                description,
                meta: props,
                facebookSocialImage:
                    data?.details?.facebookPreviewImage ?? fallbackSocialImage,
                type: 'article',
            }),
            // A noindexed page must not stay part of an hreflang cluster, or it
            // sends crawlers conflicting signals (e.g. an AU/NZ article served
            // on the US domain is noindexed there). Suppress the alternates
            // whenever the page is noindexed.
            ...getCanonicalHreflang(props, {
                professionalCountry: canonicalCountry,
                noAlternateLinks: noIndex,
            }),
            ...ldSchema,
            ...preloadImageLinks,
        ];
    },
);

export type ArticlePageLoader = SerializeFrom<typeof loader>;

export const loader = async (args: LoaderFunctionArgs) => {
    const { params, context, request } = args;
    const { user } = context;
    const isMobile = context.appDisplayMode === 'mobile';
    const link = makeArticleLink(params.article ?? '');
    const articleQuery = context.client.query<
        GetArticleDetailsQuery,
        GetArticleDetailsQueryVariables
    >({
        query: GetArticleDetailsDocument,
        variables: {
            link,
            liveOnly: true,
            editIntent: false,
        },
    });

    const [articleResult, memberRecentlyTagged, localisation, adsResponse] =
        await Promise.all([
            articleQuery,
            getMemberRecentlyTagged(args),
            getPageLocalisation(args),
            (async () => {
                // waterfall request to get the article data and feed it to the Ads API.
                // Avoid duplicated heavy ORM queries for the article data in the Ads BE.
                const articleResponse = await articleQuery;
                unsafeVariant_DO_NOT_USE(articleResponse);

                const projectItemData = await makeArticleItemData(
                    args,
                    articleResponse,
                );

                return adsRequest({
                    type: 'ProjectPlacementGroup',
                    requestUrl: request.url,
                    item: projectItemData,
                    ...args.context,
                });
            })(),
        ]);

    unsafeVariant_DO_NOT_USE(articleResult);

    const article = await assertResponse(
        context,
        articleResult.data.Directory.Article,
        articleResult.error,
    );

    const memberTagPermissions = getMemberTagPermissions(
        user,
        article.Professional?.ID || 0,
    );
    const modules = getModules(
        article,
        memberTagPermissions,
        true,
        getArticleMeta(article),
        false,
    );

    const noIndex = localisation?.NoIndex;

    // Professional team member who can edit article should also be able to see the warning
    const showPageNotLiveWarning =
        getPermissions(
            user,
            PermissionEnum.BmStaffView,
            article.Professional?.ID,
        ) ||
        getPermissions(
            user,
            PermissionEnum.BmArticleEdit,
            article.Professional?.ID,
        );

    let isAwaitingPublish = false;
    if (article.PublishDate && !isTodayOrPastDate(article.PublishDate)) {
        isAwaitingPublish = true;
    }

    // Create warning message for the WarningBanner
    const warningMessages = createWarningMessages(
        article.Professional?.Country || 'NZ',
        context.localisation.country,
        !article.IsLive ||
            article.Disable ||
            article.Professional?.Disable ||
            isAwaitingPublish,
        'article',
        false,
        {
            showPageNotLiveWarning: showPageNotLiveWarning,
            isAPStaff: getPermissions(user, PermissionEnum.BmStaffView),
        },
    );

    const ads = getProjectPlacements(adsResponse, isMobile);

    const session = await getSession(request);

    // Flag-gated in-place render, mirroring the other detail-page revamps.
    // The legacy path is untouched — revamp data is only fetched when gated
    // on, and the page builder hides the revamp-only section choices behind
    // the same gate.
    const url = new URL(request.url);
    const showRevamp = isArticleRevampEnabled(context.featureFlags, url);
    const revamp = showRevamp
        ? await getArticleRevampData(context, article, url)
        : null;

    return routerData(
        {
            localisation,
            deviceType: context.device.type,
            articleID: article.ID,
            details: getArticleDetails(article),
            banner: getVideoBanner(
                article.ID,
                article.TopBanner,
                context.device.type,
            ),
            modules,
            moduleMeta: getArticleMeta(article),
            memberPermissions: {
                canCreateTag: article?.Professional?.ID
                    ? getPermissions(
                          user,
                          PermissionEnum.BmProductTaggingCreate,
                          article.Professional.ID,
                      )
                    : false,
                canDeleteTag: article?.Professional?.ID
                    ? getPermissions(
                          user,
                          PermissionEnum.BmProductTaggingDelete,
                          article.Professional.ID,
                      )
                    : false,
                canDownloadImages: article?.Professional?.ID
                    ? getCanDownloadImages(
                          user,
                          'article',
                          article.Professional.ID,
                      )
                    : false,
            },
            memberRecentlyTagged,
            noIndex,
            ads,
            meta: article.Meta,
            warningMessages,
            revamp,
            // Premium editorial articles float the header over the full-bleed
            // title section, so the page must not reserve the mobile nav
            // clearance `default` adds — that 74px band pushes the banner down
            // and leaves the page background showing behind the transparent
            // bar. Same gate as the `header` handle below. Mobile-only in
            // effect: the clearance is already 0 from tablet up.
            pageTheme: revamp?.premiumBanner ? 'transparent' : 'default',
        },
        {
            headers: await session.getHeaders(),
        },
    );
};

export const handle: APHandle = {
    hideContactArchiPro: true,
    // Premium editorial articles float a transparent header over the full-bleed
    // banner (Figma 2479:73336). Function form so the treatment follows the
    // loader's premium gate, which already means "flags on, `?premium=1` and a
    // portrait banner uploaded" — the same condition the view renders the
    // section on. Every other article keeps the standard solid header.
    //
    // The loader data is absent whenever the route has no data to give — an
    // unknown slug throwing a 404 into the error boundary, or a navigation
    // still in flight — so this must not assume an object. AppPage resolves
    // the handle while rendering the error page too, and a throw here takes
    // the whole SSR shell down with it.
    header: (loaderData) => {
        const data = loaderData as
            | { revamp?: { premiumBanner?: boolean } | null }
            | null
            | undefined;
        return data?.revamp?.premiumBanner
            ? { headerVariant: 'transparent', showLogomark: true }
            : undefined;
    },
};

const ArticleLegacyPage = () => {
    const { desktop: isDesktop } = useAppDisplayModeContext();
    const { css } = useStyles({ isDesktop });
    useRefreshOnRotate();
    const location = useLocation();
    const { setCategory } = useCurrentCategory();

    // Set the current category for header navigation
    React.useEffect(() => {
        setCategory('Archizeen');
        return () => setCategory(null);
    }, [setCategory]);

    const {
        memberPermissions,
        memberRecentlyTagged,
        ads,
        articleID,
        moduleMeta,
    } = useLoaderData<typeof loader>();

    const { details, banner, modules } = useArticlePreviewRouteData();

    const setCountryData = useSetAtom(setCloneCountryStateAtom);

    useEffect(() => {
        setCountryData({
            urlSegment: details?.urlSegment,
            currentCountry: details?.professional?.Country,
            pageType: 'article',
            associatedPages: details?.associatedPages.edges.length
                ? details?.associatedPages
                : details?.professional?.professionalAssociatedPages,
        });
    }, [details, setCountryData]);

    const professionalDetails = {
        ID: details.professional.ID,
        Link: details.professional.link,
        CompanyLogo: details.professional.logo,
        LogoBackgroundColor: details.professional.logoBackgroundColor,
        Title: details.professional.Title,
        LibraryLocations: details.professional.libraryLocations,
        branchOptions: [],
        Website: details.professional.website,
        ArchiproEditorialAccount: details.professional.archiproEditorialAccount,
        CustomEnquiryMessage: details.professional.customEnquiryMessage,
        EnquiryMessageOverrideEnabled:
            details.professional.enquiryMessageOverrideEnabled ?? false,
        EnquiryMessagePlaceholder:
            details.professional.enquiryMessagePlaceholder,
        EnquiryAudience: details.professional.EnquiryAudience,
        primaryCTA: details.professional.primaryCTA,
        AvailableForWork: details.professional.availableForWork,
    };

    const { canCreateTag, canDeleteTag, canDownloadImages } = memberPermissions;
    const { canEditArticles } = useEditPermissions(details.professional.ID);
    const canCopyLink = usePermissions(
        PermissionEnum.BmAnalyticsView,
        details.professional.ID,
    );
    const articleEditPath = getBMPagePath(
        details.professional.URLSegment,
        'articles',
        details.urlSegment,
    );
    const bannerSize = getImagesSizes({
        desktop: FULL_COLUMN_WIDTH,
        mobile: FULL_COLUMN_WIDTH_MOBILE,
    });
    const bannerDimensions = getBannerDimensions('article');
    const canDownloadBannerImage = isDesktop && canDownloadImages;

    useSetEditMode(articleEditPath, canEditArticles);

    const hideAd = location.state?.hideAd;

    // Keep the first value returned by loader to avoid changing when loader revalidate
    const adsMemoized = useLocationMemo(hideAd ? [] : ads);
    // Render ads in the browser only (BC-1531), but keep them out of BOTH the SSR HTML and
    // the first client render so the server and client hydrate to identical DOM. The old
    // `typeof document === 'undefined' ? []` guard injected ads immediately on the client,
    // so whenever real ads existed the client's first render differed from the server — a
    // hydration mismatch (React #418) that bailed the route's client render and dropped the
    // below-fold Suspense subtree. Adding ads on the post-hydration render keeps them
    // browser-only without diverging from the server.
    const isHydrated = useHydrated();
    const modulesWithAds = useMemo(
        () =>
            getModuleWithAds(
                modules,
                isHydrated ? adsMemoized : [],
                MAX_ADS,
                AD_GAP,
            ),
        [modules, adsMemoized, isHydrated],
    );

    const { articleGatingEnabled, authBanner } = useArticleGating();

    const PickerContext = PaintPickerContextProviderV2;

    return (
        <div className={css(S.ArticlePageContainer)}>
            <Container>
                <PickerContext isEnabled={false}>
                    <ArticleDetails {...details} />
                    <DetailBannerSection
                        dimensions={bannerDimensions}
                        {...banner}
                        size={bannerSize}
                        // playsInlineVideoBanner={true}
                        // autoPlayVideoBanner={true}
                        // loopVideoBanner={false}
                        // hideVideoControls={false}
                        // showCaption={false}
                        actions={[
                            <DownloadAction
                                key="downloadAction"
                                canDownload={canDownloadBannerImage}
                                downloadLink={banner.downloadLink ?? ''}
                                libraryImageID={banner.libraryImage?.ID ?? 0}
                            />,
                        ]}
                    />
                    {details.introText && <ArticleIntro {...details} />}
                    <ProductTaggingContextProvider
                        canCreateTag={isDesktop && canCreateTag}
                        canDeleteTag={canDeleteTag}
                        recentlyTaggedProducts={memberRecentlyTagged.products}
                        recentlyTaggedProfessionals={
                            memberRecentlyTagged.professionals
                        }
                    >
                        <ModulesContextProvider meta={moduleMeta}>
                            <DetailModules
                                className={cn(
                                    'mt-14 md:mt-24 md:mb-24',
                                    articleGatingEnabled && 'md:mb-0',
                                )}
                                modules={modulesWithAds}
                                articleGatingEnabled={articleGatingEnabled}
                            />
                        </ModulesContextProvider>
                    </ProductTaggingContextProvider>
                    {!articleGatingEnabled && (
                        <DetailPageButtons
                            id={articleID}
                            editLink={articleEditPath}
                            shareableLink={`/article/${details.urlSegment}`}
                            canEdit={canEditArticles}
                            canCopyLink={canCopyLink}
                        />
                    )}
                </PickerContext>
            </Container>
            {!articleGatingEnabled && (
                <ArticleFooter
                    professional={professionalDetails}
                    articleID={articleID}
                    articleCategoryLink={details.category}
                />
            )}
            {authBanner}
        </div>
    );
};

/**
 * Maps the shared loader data into the revamp page's view model. Mirrors
 * ProjectRevampWithAdmin: the revamp skips the legacy tree's BM admin chrome,
 * so re-wire DetailPageButtons (edit-mode toggle + copy link) here for
 * logged-in members with permissions. Article auth gating stays shared with
 * the legacy tree so gated articles still show the sign-in banner and truncate
 * body modules.
 */
const ArticleRevampView = () => {
    const {
        revamp,
        articleID,
        memberPermissions,
        memberRecentlyTagged,
        moduleMeta,
    } = useLoaderData<typeof loader>();
    const { details, banner, modules } = useArticlePreviewRouteData();
    const { desktop: isDesktop } = useAppDisplayModeContext();
    const { canEditArticles } = useEditPermissions(details.professional.ID);
    const canCopyLink = usePermissions(
        PermissionEnum.BmAnalyticsView,
        details.professional.ID,
    );
    const articleEditPath = getBMPagePath(
        details.professional.URLSegment,
        'articles',
        details.urlSegment,
    );
    useSetEditMode(articleEditPath, canEditArticles);
    const { articleGatingEnabled, authBanner } = useArticleGating();
    // The legacy banner's inline-autoplay opt-in, kept so a `?video=1` link
    // lands the same way on either page.
    const { search } = useLocation();
    const autoPlayBanner =
        Number(new URLSearchParams(search).get('video')) === 1;

    if (!revamp) return null;

    const authorName = details.author.name || details.professional.Title;
    const authorOrg = details.byArchipro
        ? 'ArchiPro'
        : details.professional.Title || undefined;

    // Premium editorial title section: the revamp flags, `?premium=1` and an
    // uploaded portrait banner, mirroring how `?flexi=1` opts into the flexi
    // variant. It deliberately does not fall back to the landscape TopBanner,
    // whose crop cannot fill this section's tall frame without being zoomed, so
    // an article without the portrait upload renders the standard variant
    // instead. `featuring` reuses the professional as a stand-in until its own
    // BM field lands.
    const premiumHero =
        revamp.premiumBanner && details.premiumBannerImageUrl
            ? {
                  bannerImageUrl: details.premiumBannerImageUrl,
                  alt: `${details.title} Premium Banner`,
                  title: details.title,
                  featuring: revamp.professional?.title,
                  writtenBy: authorName || undefined,
                  publishDate: details.publishDate || undefined,
                  subheading: details.premiumBannerSubheading,
              }
            : undefined;

    return (
        // The image modules carry the legacy photo hover actions, so they need
        // the same three contexts the legacy tree mounts: tag authoring, the
        // module meta its editing surfaces read, and the paint picker the hover
        // buttons read (mounted disabled, as articles never offered the Resene
        // tool).
        <PaintPickerContextProviderV2 isEnabled={false}>
            <ProductTaggingContextProvider
                canCreateTag={isDesktop && memberPermissions.canCreateTag}
                canDeleteTag={memberPermissions.canDeleteTag}
                recentlyTaggedProducts={memberRecentlyTagged.products}
                recentlyTaggedProfessionals={memberRecentlyTagged.professionals}
            >
                <ModulesContextProvider meta={moduleMeta}>
                    {!articleGatingEnabled && (
                        <DetailPageButtons
                            id={articleID}
                            editLink={articleEditPath}
                            shareableLink={`/article/${details.urlSegment}`}
                            canEdit={canEditArticles}
                            canCopyLink={canCopyLink}
                        />
                    )}
                    <ArticleRevampPage
                        variant={revamp.variant}
                        header={{
                            label: authorName ? `By ${authorName}` : undefined,
                            title: details.title,
                            categories: revamp.categories,
                        }}
                        author={
                            authorName
                                ? {
                                      name: authorName,
                                      org:
                                          authorOrg !== authorName
                                              ? authorOrg
                                              : undefined,
                                      avatarUrl:
                                          details.author.avatar || undefined,
                                  }
                                : undefined
                        }
                        teaser={details.introText || undefined}
                        hero={
                            banner.imageUrl
                                ? {
                                      imageUrl: banner.imageUrl,
                                      // The banner's own description, not the headline
                                      // — the h1 beside it already announces that, and
                                      // repeating it reads the title twice. Falls back
                                      // to decorative when the image has no caption.
                                      alt: banner.caption ?? '',
                                      caption: banner.caption,
                                      videoUrl:
                                          banner.isVideoBanner &&
                                          banner.videoBannerUrl
                                              ? banner.videoBannerUrl
                                              : undefined,
                                      autoplay: autoPlayBanner,
                                      libraryImageId: banner.libraryImage?.ID,
                                      libraryItemId: banner.libraryItemId,
                                      wasUpscaled:
                                          !!banner.libraryImage?.WasUpscaled,
                                      downloadLink: banner.downloadLink,
                                  }
                                : undefined
                        }
                        credits={revamp.credits}
                        professional={revamp.professional}
                        discoverTiles={revamp.discoverTiles}
                        downloads={revamp.downloads}
                        articleID={articleID}
                        shareLink={`/article/${details.urlSegment}`}
                        enquiryProfessional={revamp.enquiryProfessional}
                        modules={modules as PageBuilderModuleType[]}
                        articleGatingEnabled={articleGatingEnabled}
                        premiumHero={premiumHero}
                        flexiTitle={revamp.flexiTitle}
                        showNewsletterSignup={details.showNewsletterSignup}
                        publishDate={details.publishDate || undefined}
                    />
                    {authBanner}
                </ModulesContextProvider>
            </ProductTaggingContextProvider>
        </PaintPickerContextProviderV2>
    );
};

/**
 * Switches between the legacy article page and the editorial revamp behind
 * both the `article_detail_revamp_2026` and `details_revamp_2026` CMS feature
 * flags (`?revamp=1` in local dev). The loader is shared; the legacy tree is
 * untouched when the gate is off.
 */
const ArticlePage = () => {
    const { revamp } = useLoaderData<typeof loader>();
    return revamp ? <ArticleRevampView /> : <ArticleLegacyPage />;
};

export default ArticlePage;
